"use client";

import { useUser } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSetLastCartId } from "@/features/cart/api/hooks/use-set-last-cart-id";
import { useCartStore } from "@/features/cart/components";
import { useMultidietCartStore } from "@/features/multidiet/components/multidiet-cart-provider";
import { setObfuscatedLocalStorage } from "@/lib/storage";
import type { Order } from "../types/order.type";
import type { Payment } from "../types/order-payment.type";
import { clearPendingPayment, isTerminalPaymentStatus } from "../utils";

export const usePaymentCompletionCleanup = () => {
  const cartStore = useCartStore((state) => state);
  const multidietCartStore = useMultidietCartStore((state) => state);
  const queryClient = useQueryClient();
  const { isSignedIn } = useUser();
  const { mutate: setLastCartId } = useSetLastCartId();

  return useCallback(
    ({
      orderType,
      paymentStatus,
    }: {
      orderType: Order["type"];
      paymentStatus: Payment["status"];
    }) => {
      if (paymentStatus === "COMPLETED") {
        const invalidateUser = () =>
          queryClient.invalidateQueries({ queryKey: ["user"] });

        if (orderType === "multidiet") {
          multidietCartStore.resetCart();
        } else {
          cartStore.resetCart();
        }

        setObfuscatedLocalStorage<Record<string, never>>("contactForm", {});

        // buyer.lastCartId still points at the cart that was just paid for.
        // Left there, the cart merge would adopt it back after the reset. The
        // user is refetched only once the backend has forgotten it, otherwise
        // the merge could read the stale id first.
        if (orderType !== "multidiet" && isSignedIn) {
          setLastCartId({ cartId: null }, { onSettled: invalidateUser });
        } else {
          invalidateUser();
        }
      }

      if (isTerminalPaymentStatus(paymentStatus)) {
        clearPendingPayment();
      }
    },
    [cartStore, isSignedIn, multidietCartStore, queryClient, setLastCartId],
  );
};
